/* eslint-disable react/no-unstable-nested-components */
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@hortis/ui/collapsible";
import { Divider } from "@hortis/ui/divider";
import { ChevronRight } from "@hortis/ui/icons";
import {
  PortableText as PortableTextPrimitive,
  type PortableTextProps,
} from "@portabletext/react";
import type { ReactNode } from "react";

export const PortableText = (props: PortableTextProps) => {
  return (
    <PortableTextPrimitive
      {...props}
      components={{
        block: {
          normal: ({ children }) => {
            return (
              <p className="mb-3 text-sm text-grey-500 last-of-type:mb-5">
                {children}
              </p>
            );
          },
          h1: ({ children }) => {
            return (
              <h2 className="group mb-3 mt-6 flex items-center gap-2 font-header text-2xl font-semibold tracking-tighter text-grey-900">
                {children}
              </h2>
            );
          },
          h2: ({ children }) => {
            return (
              <h2 className="group mb-3 mt-6 flex items-center gap-2 font-header text-2xl font-semibold tracking-tighter text-grey-900">
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            return (
              <h3 className="group mt-4 flex items-center gap-2 font-header text-lg font-semibold tracking-tighter text-grey-900">
                {children}
              </h3>
            );
          },
          h4: ({ children }) => {
            return (
              <h4 className="group mt-4 flex items-center gap-2 font-header text-lg font-semibold tracking-tighter text-grey-900">
                {children}
              </h4>
            );
          },
        },
        marks: {
          link: ({
            children,
            value,
          }: {
            value?: { href: string };
            children: ReactNode;
          }) => {
            return (
              <a href={value?.href} className="text-primary-700">
                {children}
              </a>
            );
          },
        },
        list: {
          bullet: ({ children }) => (
            <ul className="mt-xl mb-6 list-disc text-sm text-grey-500">
              {children}
            </ul>
          ),
          number: ({ children }) => (
            <ol className="mt-lg mb-6 pl-4  text-sm text-grey-500">
              {children}
            </ol>
          ),
        },
        listItem: {
          bullet: ({ children }) => (
            <li className="mb-3 ml-6 [&::marker]:text-grey-400">{children}</li>
          ),
        },
        types: {
          note: ({ value }: { value: { note: string } }) => {
            return (
              <div className="my-3 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5 text-sm font-medium text-grey-500">
                {value.note}
              </div>
            );
          },
          break: () => <Divider />,
          collapsible: ({
            value,
          }: {
            value: { label: string; content: PortableTextProps["value"] };
          }) => {
            return (
              <div className="mb-2 rounded-lg border-grey-200 bg-grey-100 px-4 py-3 text-sm">
                <Collapsible>
                  <CollapsibleTrigger className="flex w-full items-center gap-2 font-medium text-grey-700 [&[data-state=open]_.chevron]:rotate-90">
                    <ChevronRight className="chevron text-grey-500 transition" />
                    {value.label}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4 [&_li::marker]:text-grey-700 [&_li]:ml-5 [&_ul]:m-0">
                    <PortableText value={value.content} />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          },
          image: (img: {
            value: {
              asset: { url: string };
              captionComplex: PortableTextProps["value"];
            };
          }) => {
            return (
              <figure className="my-6 flex flex-col items-center">
                <img
                  src={`${img.value.asset.url}?auto=format`}
                  alt="Feature"
                  className="overflow-hidden rounded-xl"
                />
                <PortableTextPrimitive
                  value={img.value.captionComplex}
                  components={{
                    block: {
                      normal: ({ children }) => {
                        return (
                          <caption className="mt-2 text-center text-sm text-grey-500">
                            {children}
                          </caption>
                        );
                      },
                    },
                  }}
                />
              </figure>
            );
          },
        },
      }}
    />
  );
};
